import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import md5 from 'blueimp-md5';
import Gravatar from 'react-gravatar';

import {
  PaginationTable,
  StatusCell,
  DateCell,
  TableLoader,
  ActionsButton,
  RowLink,
  BOPaginationTable,
  SwitchTableVariantButtons,
} from 'common/components';
import { useToggleTableVariant } from 'common/hooks';
import { getNewTableColumns } from 'common/utils';
import UserActivity from 'backoffice/_components/UserActivity';
import { withTheme } from 'common/styling/theme';
import { PhoneIcon } from 'backoffice/managers/_components/TaskIcon/icons';
import ProvidersPopup from '../ProvidersPopup';
import { staticStyles, getDynamicStyles } from './style';

const BOUsersTable = ({
  data,
  onRowClick,
  loading,
  location,
  history,
  params,
  getList,
  hasPaymentOperationManagers,
  kycSettings,
  theme,
  addons,
  profilePerms,
  withManagers,
  withStatuses,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const [isModalOpen, setModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [modalId, setModalId] = useState(0);
  const [buttonPosition, setButtonPosition] = useState([0, 0]);
  const withClick2Call = (!!profilePerms.managerViewUser || !!profilePerms.viewUser) && addons.includes('click2call');
  const withManagersAndCall = withManagers && withClick2Call;

  const { tableVariant, handleSelectNewVariant, handleSelectLegacyVariant } = useToggleTableVariant({
    tableKey: 'BOUsersTable',
  });

  const paginationTableRef = useRef(null);

  useEffect(() => {
    if (window.innerWidth < 768) {
      setIsMobile(true);
    }
  }, []);

  const getPopupPosition = (buttonPosition, tablePosition) => {
    const topDiff = isMobile ? 30 : 0;
    const rightDiff = isMobile ? 80 : 210;
    const top = buttonPosition.y - tablePosition.y + topDiff;
    const right = buttonPosition.x - tablePosition.x - rightDiff;
    setButtonPosition([top, right]);
  };

  const toggleModalOpen = (id, elem, isHover) => {
    if (isHover) {
      setModalOpen(true);
    } else {
      setModalOpen(!isModalOpen);
    }

    if (id) {
      setModalId(id);
      getPopupPosition(elem.getBoundingClientRect(), paginationTableRef.current.getBoundingClientRect());
    }
  };

  /* eslint-disable */
  const columns = [
    {
      Header: () => null,
      id: 'avatar',
      Cell: ({
        row: {
          original: { email, id },
        },
      }) => (
        <RowLink link={`/backoffice/users/${id}`} isPreviewCell>
          <Gravatar md5={md5(email)} size={35} />
        </RowLink>
      ),
      isPreviewCell: true,
      size: 60,
    },
    {
      Header: 'justFullName',
      Cell: ({
        row: {
          original: { fullName, id },
        },
      }) => (
        <RowLink className="BOUsersTable__fullName" link={`/backoffice/users/${id}`} isPreviewCell>
          {fullName}
        </RowLink>
      ),
      isPreviewCell: true,
      size: 180,
    },
    {
      Header: 'justEmail',
      Cell: ({
        row: {
          original: { email, id },
        },
      }) => <RowLink link={`/backoffice/users/${id}`}>{email}</RowLink>,
      isPreviewDescriptionCell: true,
      size: 180,
    },
    {
      Header: 'justBrand',
      Cell: ({
        row: {
          original: { brandTitle, id },
        },
      }) => (
        <RowLink link={`/backoffice/users/${id}`}>
          <span>{brandTitle || '—'}</span>
        </RowLink>
      ),
      size: 140,
    },
    {
      Header: 'justPhone',
      Cell: ({
        row: {
          original: { phone, id },
        },
      }) => (
        <RowLink link={`/backoffice/users/${id}`} isPreviewCell>
          {phone}
        </RowLink>
      ),
      isPreviewCell: true,
      size: 140,
    },
    {
      Header: 'justDateJoined',
      Cell: ({
        row: {
          original: { dateJoined, id },
        },
      }) => (
        <RowLink link={`/backoffice/users/${id}`}>
          <DateCell value={dateJoined} />
        </RowLink>
      ),
      size: 140,
    },
    ...(hasPaymentOperationManagers
      ? [
          {
            Header: 'justManager',
            Cell: ({
              row: {
                original: { manager, id },
              },
            }) => (
              <RowLink link={`/backoffice/users/${id}`}>
                <span>{manager ? `${manager.firstName} ${manager.lastName}` : '一'}</span>
              </RowLink>
            ),
          },
        ]
      : []),
    {
      Header: 'justPartner',
      Cell: ({
        row: {
          original: { partnerAccount, id },
        },
      }) => (
        <RowLink link={`/backoffice/users/${id}`}>
          <span>{partnerAccount ?? '一'}</span>
        </RowLink>
      ),
      size: 140,
    },
    {
      Header: 'justIbAccounts',
      Cell: ({
        row: {
          original: { ibAccounts, id },
        },
      }) => (
        <RowLink link={`/backoffice/users/${id}`}>
          <span className="BOUsersTable__IbAccounts">
            {ibAccounts && ibAccounts.length
              ? ibAccounts.map((ibAccount, ind) => (
                  <span key={`BOUsersTable__IbAccount-${ind}`} className="BOUsersTable__IbAccount">
                    {ibAccount}
                  </span>
                ))
              : '一'}
          </span>
        </RowLink>
      ),
    },
    ...(kycSettings.enabled
      ? [
          {
            Header: 'justVerificationStatus',
            Cell: ({
              row: {
                original: { status, id },
              },
            }) => (
              <RowLink link={`/backoffice/users/${id}`}>
                <StatusCell statusCode={status} noDot />
              </RowLink>
            ),
          },
        ]
      : []),
    ...(withStatuses
      ? [
          {
            Header: 'boStatus',
            Cell: ({
              row: {
                original: { currentStatus, id },
              },
            }) => (
              <RowLink link={`/backoffice/users/${id}`}>
                <span>{currentStatus || '一'}</span>
              </RowLink>
            ),
          },
        ]
      : []),
    {
      Header: 'kycIsActive',
      Cell: ({
        row: {
          original: { isActive, isOnline, lastSeen, id },
        },
      }) => (
        <RowLink link={`/backoffice/users/${id}`}>
          <UserActivity isBlocked={!isActive} isOnline={isOnline} lastSeen={lastSeen} onlyDot />
        </RowLink>
      ),
      size: 100,
    },
    ...(withClick2Call
      ? [
          {
            Header: 'justCall',
            Cell: ({
              row: {
                original: { id },
              },
            }) => (
              <ActionsButton
                onClickFunc={evt => {
                  evt.stopPropagation();
                  toggleModalOpen(id, evt.target);
                }}
                onMouseEnterFunc={evt => {
                  evt.stopPropagation();
                  toggleModalOpen(id, evt.target, true);
                }}>
                <PhoneIcon />
              </ActionsButton>
            ),
          },
        ]
      : []),
  ];
  /* eslint-enable */

  const newTableColumns = useMemo(() => getNewTableColumns(columns), []);

  return (
    <div
      className={classNames(
        'BOUsersTable',
        {
          'BOUsersTable--withManagers': withManagers,
        },
        {
          'BOUsersTable--withManagersAndCall': withManagersAndCall,
        },
        {
          'BOUsersTable--withStatuses': withStatuses,
        }
      )}
      onMouseLeave={evt => {
        evt.stopPropagation();
        setModalOpen(false);
      }}
      ref={paginationTableRef}>
      <SwitchTableVariantButtons
        tableVariant={tableVariant}
        handleSelectNewVariant={handleSelectNewVariant}
        handleSelectLegacyVariant={handleSelectLegacyVariant}
      />

      {tableVariant === 'legacy' && (
        <>
          {loading && <TableLoader />}
          {!loading && (
            <PaginationTable
              data={data}
              columns={columns}
              loading={loading}
              getList={getList}
              location={location}
              history={history}
              params={params}
              onRowClick={onRowClick}
            />
          )}
        </>
      )}

      {tableVariant === 'new' && (
        <BOPaginationTable
          tableKey="BOUsersTable"
          data={data}
          columns={newTableColumns}
          params={params}
          getList={getList}
          loading={loading}
        />
      )}

      {isModalOpen ? (
        <ProvidersPopup id={modalId} buttonPosition={buttonPosition} toggleModalOpen={toggleModalOpen} />
      ) : null}

      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </div>
  );
};

BOUsersTable.propTypes = {
  data: PropTypes.shape({
    count: PropTypes.number,
    results: PropTypes.arrayOf(
      PropTypes.shape({
        fullName: PropTypes.string,
        email: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        isActive: PropTypes.bool.isRequired,
        dateJoined: PropTypes.string.isRequired,
      })
    ),
  }),
  onRowClick: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  getList: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  kycSettings: PropTypes.object.isRequired,
  hasPaymentOperationManagers: PropTypes.bool,
  withStatuses: PropTypes.bool,
  theme: PropTypes.object,
  addons: PropTypes.array,
  profilePerms: PropTypes.object,
};

BOUsersTable.defaultProps = {
  data: {},
  loading: false,
  hasPaymentOperationManagers: false,
  withStatuses: false,
  theme: {},
  addons: [],
  profilePerms: {},
};

export default withTheme()(BOUsersTable);
export { BOUsersTable };
